import { useInfiniteQuery } from "@tanstack/react-query";
import adminApiClient, {
  ClockSortDirection,
  ClockSortField,
  GetClocksInput,
} from "../../../api/admin";
const PAGE_SIZE = 100;

export type ClocksInfiniteQueryInput = {
  query: string;
  onlyDevClocks?: boolean;
  onlyBetaClocks?: boolean;
  onlyArtistClocks?: boolean;
  registrationStatus?: "ALL" | "NOT_REGISTERED" | "REGISTERED";
  fromVersion?: number;
  toVersion?: number;
  heartbeatMinutes?: number;
  country?: string;
  city?: string;
  region?: string;
  timeZone?: string;
  mode?: string;
  cycle?: string;
  settings?: string[];
  ids?: string[];
  monitorId?: string;
  featureBetaGroupId?: string;
  sortBy?: ClockSortField;
  sortDir?: ClockSortDirection;
};

const useClocksInfiniteQuery = ({
  query,
  onlyDevClocks,
  onlyBetaClocks,
  onlyArtistClocks,
  registrationStatus,
  fromVersion,
  toVersion,
  heartbeatMinutes,
  country,
  city,
  region,
  timeZone,
  mode,
  cycle,
  settings,
  ids,
  monitorId,
  featureBetaGroupId,
  sortBy,
  sortDir,
}: ClocksInfiniteQueryInput) => {
  const { data: machinesNewData, ...rest } = useInfiniteQuery({
    queryKey: [
      "machines",
      query,
      onlyDevClocks,
      onlyBetaClocks,
      onlyArtistClocks,
      registrationStatus,
      fromVersion,
      toVersion,
      heartbeatMinutes,
      country,
      city,
      region,
      timeZone,
      mode,
      cycle,
      settings,
      ids,
      monitorId,
      featureBetaGroupId,
      sortBy,
      sortDir,
    ],
    queryFn: async ({ pageParam }) => {
      const { skip = 0 } = pageParam as { skip?: number };
      const input: GetClocksInput = {
        skip,
        limit: PAGE_SIZE,
        query,
        devClocks: onlyDevClocks,
        betaClocks: onlyBetaClocks,
        artistClocks: onlyArtistClocks,
        registrationStatus,
        fromVersion,
        toVersion,
        heartbeatMinutes,
        country,
        city,
        region,
        timeZone,
        mode,
        cycle,
        settings,
        ids,
        monitorId,
        featureBetaGroupId,
        sortBy,
        sortDir,
      };
      const res = await adminApiClient.getClocks(input);

      return res;
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_SIZE
        ? undefined
        : { skip: allPages.flat().length },
    initialPageParam: { skip: 0 },
  });
  const firstPage = machinesNewData?.pages[0];
  return {
    data: machinesNewData?.pages.flat(),
    // Every row carries `totalCount` (matches ignoring pagination); undefined
    // if the API doesn't send it, 0 when the first page is empty.
    totalCount:
      firstPage?.length === 0
        ? 0
        : (firstPage?.[0] as { totalCount?: number } | undefined)?.totalCount,
    ...rest,
  };
};

export default useClocksInfiniteQuery;
